/**
 * VerificationInput Molecule
 *
 * A 6-digit code input for verification purposes (OTP, email codes, etc).
 * Combines multiple Input atoms with auto-focus logic.
 */

import { Input } from '@/components/atoms/inputs';
import React, { ChangeEvent, useEffect, useState } from 'react';

interface VerificationInputProps {
  onChange?: (code: string) => void;
  onComplete?: (code: string) => void;
}

const VerificationInput: React.FC<VerificationInputProps> = ({ onChange, onComplete }) => {
  const [code, setCode] = useState<Array<string>>(Array(6).fill(''));
  const [inputsRef, setInputsRef] = useState<Array<React.RefObject<HTMLInputElement>>>([]);

  useEffect(() => {
    setInputsRef(
      Array(6)
        .fill(0)
        .map(() => React.createRef<HTMLInputElement>() as React.RefObject<HTMLInputElement>)
    );
  }, []);

  useEffect(() => {
    const areAllDigitsFilled = code.every((digit) => digit !== '');
    const codeAsString = code.join('');

    onChange?.(codeAsString);

    if (areAllDigitsFilled) {
      onComplete?.(codeAsString);
    }
  }, [code, onChange, onComplete]);

  const updateCode = (digit: string, i: number) => {
    setCode((prevCode) => [...prevCode.slice(0, i), digit, ...prevCode.slice(i + 1)]);
  };

  const focusInput = (i: number) => {
    inputsRef[i].current?.focus();
  };

  const handleChange = (digit: string, i: number) => {
    const firstEmptyIndex = code.findIndex((c) => c === '');
    const isDeletingMiddleInput = digit === '' && i > 0 && i < code.length - 1;
    const isLastInput = i === code.length - 1;
    const isTypingAndNextInputExists = digit !== '' && inputsRef[i + 1];

    if (firstEmptyIndex !== -1 && firstEmptyIndex < i) {
      focusInput(firstEmptyIndex);
      return;
    }

    if (/[^0-9]/.test(digit) && digit !== '') {
      return;
    }

    updateCode(digit, i);

    if (isDeletingMiddleInput && isLastInput) {
      focusInput(i - 1);
    } else if (isTypingAndNextInputExists) {
      focusInput(i + 1);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, i: number) => {
    if (e.key === 'Backspace' && code[i] === '' && i > 0) {
      updateCode('', i - 1);
      focusInput(i - 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);

    if (pastedData.length > 0) {
      const newCode = [...code];
      for (let i = 0; i < pastedData.length && i < 6; i++) {
        newCode[i] = pastedData[i];
      }
      setCode(newCode);

      // Focus the next empty input or the last one
      const nextEmptyIndex = newCode.findIndex((c) => c === '');
      if (nextEmptyIndex !== -1 && inputsRef[nextEmptyIndex]) {
        focusInput(nextEmptyIndex);
      } else if (inputsRef[5]) {
        focusInput(5);
      }
    }
  };

  return (
    <div className='flex items-center gap-2'>
      {inputsRef.map((inputRef, i) => (
        <Input
          wrapperClassName='w-full aspect-square max-w-12'
          className='h-full max-h-[80px] w-full max-w-[80px] border border-zinc-300 text-center text-xl font-medium placeholder:text-zinc-300 focus:border-2 disabled:bg-zinc-100 disabled:text-zinc-300'
          key={i}
          type='tel'
          maxLength={1}
          ref={inputRef}
          value={code[i]}
          placeholder={'-'}
          onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange(e.target.value, i)}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, i)}
          onPaste={handlePaste}
        />
      ))}
    </div>
  );
};

export { VerificationInput };
export default VerificationInput;
